/*
 * Detail page for a TechRadar entity (`/tech-radars/:namespace/:kind/:name`).
 *
 * Ported from the original `feat/techradar-backstage` implementation to the
 * New Frontend System (Backstage 1.50) as a routable `PageBlueprint`. The
 * inner page-shell (`TechRadarDetailPageContent`) is a faithful copy of the
 * original: same breadcrumb trail, header (title + description +
 * Scope/Owner/Contact rows), tabs (Tech Radar + conditional Documentation),
 * and context menu (favorite + 3-dots), all composed with `PageDetailLayout`.
 *
 * Difference from the original: the original rendered inside the catalog
 * entity page, where an ambient `useAsyncEntity()` provider already existed.
 * On a standalone route there is no such provider, so the outer
 * `TechRadarDetailPage` loads the entity itself from the URL params (via
 * `catalogApiRef`) and wraps the content in `<AsyncEntityProvider>` so the
 * inner `useAsyncEntity()` resolves exactly as before.
 *
 * Layered structure:
 *   <AsyncEntityProvider {entity, loading, error}>   — provides useAsyncEntity
 *     <Page themeId="...">                           — Backstage theme integration
 *       <PageDetailLayout>                           — outer spacing owner
 *         <PageDetailLayout.Header>
 *           <PageHeader ...>                       — native BUI Header owns its inset
 *         </PageDetailLayout.Header>
 *         <PageDetailLayout.Tabs>
 *           <RoutedTabs ...>                         — Tech Radar / Documentation
 *         </PageDetailLayout.Tabs>
 *         <PageDetailLayout.Content>(routed)</PageDetailLayout.Content>
 *       </PageDetailLayout>
 *     </Page>
 *   </AsyncEntityProvider>
 *
 * The Scope/Owner/Contact rows are passed via the `descriptions` prop on
 * `PageHeader` (kind-specific accessors live in
 * `lib/build-descriptions.ts`). The subtitle line is filled by passing
 * `entity.metadata.description` to PageHeader's `description` prop.
 */

import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import useAsync from 'react-use/esm/useAsync';
import {
  type CompoundEntityRef,
  type Entity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { compatWrapper } from '@backstage/core-compat-api';
import {
  Link,
  Page,
  Progress,
  ResponseErrorPanel,
  WarningPanel,
} from '@backstage/core-components';
import { errorApiRef, useApi } from '@backstage/core-plugin-api';
import { PageLayout } from '@backstage/frontend-plugin-api';
import {
  AsyncEntityProvider,
  catalogApiRef,
  InspectEntityDialog,
  UnregisterEntityDialog,
  useAsyncEntity,
} from '@backstage/plugin-catalog-react';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { NS_API_VERSION } from '@ns/plugin-ns-tech-radar-common';
import {
  EntityContextMenu,
  PageDetailLayout,
  PageHeader,
  useRoutedTabs,
} from '@ns/plugin-ns-theme';

import {
  buildTechRadarDescriptions,
  hasTechDocs,
} from '../../lib/build-descriptions';
import { TechRadarEntityOverviewContent } from '../tech-radar-entity-overview-content';

type InspectInitialTab =
  | 'overview'
  | 'ancestry'
  | 'colocated'
  | 'json'
  | 'yaml';
const INSPECT_INITIAL_TABS: readonly InspectInitialTab[] = [
  'overview',
  'ancestry',
  'colocated',
  'json',
  'yaml',
];

const isInspectInitialTab = (value: string): value is InspectInitialTab =>
  (INSPECT_INITIAL_TABS as readonly string[]).includes(value);

/**
 * Coerce a raw `?inspect=` query-string value to a valid `InspectEntityDialog`
 * tab id. Returns `undefined` for empty/null/invalid values so the dialog
 * falls back to its own default tab. Exported for unit-testing.
 */
export const toInspectInitialTab = (
  raw: string | null,
): InspectInitialTab | undefined => {
  if (!raw) return undefined;
  return isInspectInitialTab(raw) ? raw : undefined;
};

function fallbackEntityFromParams(
  kind: string | undefined,
  namespace: string | undefined,
  name: string | undefined,
): Entity | undefined {
  if (!kind || !name) {
    return undefined;
  }
  return {
    // Use the NS TechRadar apiVersion so `isTechRadarEntity()` returns true
    // for the fallback header rendered before the real entity loads (or when
    // the lookup fails). Otherwise downstream debug/inspection wrongly
    // suggests this is a generic Backstage entity.
    apiVersion: NS_API_VERSION,
    kind,
    metadata: {
      name,
      ...(namespace && namespace !== 'default' ? { namespace } : {}),
    },
    spec: {},
  };
}

/**
 * TechRadar detail page shell — composes PageDetailLayout directly (no
 * EntityLayout wrapper) so spacing ownership stays at the layout level.
 *
 * Consumes the ambient entity through `useAsyncEntity()`; the surrounding
 * `TechRadarDetailPage` supplies that context via `AsyncEntityProvider`.
 */
export function TechRadarDetailPageContent() {
  // Read URL params directly so a fallback Header can render before the
  // entity loads. Matches the original EntityLayout behaviour.
  const { kind, name, namespace } = useParams();
  const { entity, error, loading } = useAsyncEntity();
  const location = useLocation();
  const errorApi = useApi(errorApiRef);

  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    setConfirmationDialogOpen(false);
  }, [location.pathname]);

  // Forward entity-load failures to the global error API so Datadog (and any
  // other ErrorApi consumer) captures them. ResponseErrorPanel handles the
  // user-facing rendering.
  useEffect(() => {
    if (error) {
      errorApi.post(error);
    }
  }, [error, errorApi]);

  const fallbackEntity = fallbackEntityFromParams(kind, namespace, name);
  const headerEntity = entity ?? fallbackEntity;

  const cleanUpAfterRemoval = async () => {
    setConfirmationDialogOpen(false);
    navigate('/catalog');
  };

  const selectedInspectTab = searchParams.get('inspect');
  const showInspectTab = typeof selectedInspectTab === 'string';

  const descriptions = entity ? buildTechRadarDescriptions(entity) : [];

  // Build the tab routes. Tab-strip and routed content are produced by
  // `useRoutedTabs` so each can land in its own PageDetailLayout slot.
  const tabRoutes = useMemo(
    () =>
      entity
        ? [
            {
              children: <TechRadarEntityOverviewContent />,
              path: '/',
              title: 'Tech Radar',
            },
            ...(hasTechDocs(entity)
              ? [
                  {
                    // EntityTechdocsContent is a legacy-era component (it
                    // calls useRouteRef from core-plugin-api). On a standalone
                    // NFS route there is no compat-routing context unless we
                    // wrap with compatWrapper — the same bridge the framework
                    // uses to mount third-party legacy content extensions (see
                    // soundcheck `entity-content.esm.js`). Without this wrap the
                    // /docs sub-route throws "Routing context is not available".
                    children: compatWrapper(<EntityTechdocsContent />),
                    path: '/docs',
                    title: 'Documentation',
                  },
                ]
              : []),
          ]
        : [],
    [entity],
  );

  const {
    content: routedElement,
    selectedTitle,
    tabStrip,
  } = useRoutedTabs(tabRoutes);

  return (
    <Page
      themeId={
        typeof entity?.spec?.type === 'string' ? entity.spec.type : 'home'
      }
    >
      <PageDetailLayout>
        {headerEntity && (
          <PageDetailLayout.Header>
            <PageHeader
              actions={
                <EntityContextMenu
                  onInspectEntity={() => {
                    setSearchParams(prev => {
                      const newParams = new URLSearchParams(prev);
                      newParams.set('inspect', '');
                      return newParams;
                    });
                  }}
                  onUnregisterEntity={() => setConfirmationDialogOpen(true)}
                />
              }
              description={headerEntity.metadata.description}
              descriptions={descriptions}
              title={headerEntity.metadata.title ?? headerEntity.metadata.name}
              typeTag="techradar"
            />
          </PageDetailLayout.Header>
        )}

        {entity && tabStrip && (
          <PageDetailLayout.Tabs>{tabStrip}</PageDetailLayout.Tabs>
        )}

        <PageDetailLayout.Content>
          {loading && <Progress />}

          {error && <ResponseErrorPanel error={error} />}

          {!loading && !error && !entity && (
            <WarningPanel title="Entity not found">
              The requested entity could not be found in the catalog. Please
              consult the{' '}
              <Link to="https://backstage.io/docs/features/software-catalog/references">
                catalog documentation
              </Link>{' '}
              for help.
            </WarningPanel>
          )}

          {entity && routedElement && (
            <>
              <Helmet title={selectedTitle} />
              {routedElement}
            </>
          )}
        </PageDetailLayout.Content>
      </PageDetailLayout>

      {showInspectTab && entity && (
        <InspectEntityDialog
          open
          entity={entity}
          initialTab={toInspectInitialTab(selectedInspectTab)}
          onClose={() =>
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.delete('inspect');
              return newParams;
            })
          }
          onSelect={newTab =>
            setSearchParams(prev => {
              const newParams = new URLSearchParams(prev);
              newParams.set('inspect', newTab);
              return newParams;
            })
          }
        />
      )}

      {entity && (
        <UnregisterEntityDialog
          entity={entity}
          onClose={() => setConfirmationDialogOpen(false)}
          onConfirm={cleanUpAfterRemoval}
          open={confirmationDialogOpen}
        />
      )}
    </Page>
  );
}

/**
 * Routable entry point for the standalone TechRadar detail route. Loads the
 * entity referenced by the URL params from the catalog and exposes it to the
 * inner shell via `AsyncEntityProvider`, so the ported
 * `TechRadarDetailPageContent` (which relies on `useAsyncEntity()`) behaves
 * identically to its catalog-entity-page origin.
 */
export function TechRadarDetailPage() {
  const { kind, name, namespace } = useParams();
  const catalogApi = useApi(catalogApiRef);

  const {
    error,
    loading,
    value: entity,
  } = useAsync(async () => {
    if (!kind || !name) {
      return;
    }
    const entityRef: CompoundEntityRef = {
      kind,
      name,
      namespace: namespace ?? 'default',
    };
    return catalogApi.getEntityByRef(stringifyEntityRef(entityRef));
  }, [catalogApi, kind, name, namespace]);

  return (
    <AsyncEntityProvider entity={entity} error={error} loading={loading}>
      <PageLayout title="Tech radars" titleLink="/tech-radars">
        <TechRadarDetailPageContent />
      </PageLayout>
    </AsyncEntityProvider>
  );
}
